import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Eye,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  LayoutGrid,
  BarChart3,
} from 'lucide-react'
import { NewCaseModal } from './NewCaseModal'
import { CaseWorkView } from './CaseManagementDashboard.work-view'
import { EditCaseModal } from './EditCaseModal'
import { CaseQuotaIndicator } from './CaseQuotaIndicator'
import { SummaryView } from '../dashboard/SummaryView'
import { useCaseDashboard } from './use-case-management-dashboard'
import type { ViewMode, StatusTab } from './use-case-management-dashboard'
import type { CaseQuotaStatus } from '../../types'

interface StatusTabButtonProps {
  tab: StatusTab
  activeTab: StatusTab
  label: string
  count: number
  onSelect: (tab: StatusTab) => void
}

export function StatusTabButton({
  tab,
  activeTab,
  label,
  count,
  onSelect,
}: StatusTabButtonProps): ReactElement {
  const isActive = activeTab === tab
  return (
    <button
      onClick={() => { onSelect(tab) }}
      className="relative px-4 py-2.5 text-sm font-medium transition-colors"
      style={{ color: isActive ? 'rgb(101, 163, 13)' : 'var(--text-muted)' }}
    >
      {label}
      <span
        className="ml-2 rounded-full px-2 py-0.5 text-xs"
        style={
          isActive
            ? { background: 'rgba(132, 204, 22, 0.12)', color: 'rgb(77, 124, 15)' }
            : { background: 'var(--highlight)', color: 'var(--text-dim)' }
        }
      >
        {count}
      </span>
      {isActive && (
        <div
          className="absolute right-0 bottom-0 left-0 h-0.5"
          style={{ background: 'rgb(101, 163, 13)' }}
        />
      )}
    </button>
  )
}

interface DashboardTitleSectionProps {
  viewMode: ViewMode
  onViewMode: (mode: ViewMode) => void
}

function DashboardTitleSection({ viewMode, onViewMode }: DashboardTitleSectionProps): ReactElement {
  const { t } = useTranslation(['cases', 'common'])
  const viewBtnStyle = (mode: ViewMode): React.CSSProperties =>
    viewMode === mode
      ? { background: 'var(--bg-card)', color: 'var(--text)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }
      : { color: 'var(--text-muted)' }
  return (
    <div className="flex items-center gap-6">
      <div>
        <h1
          className="text-2xl font-bold"
          style={{ color: 'var(--text)', fontFamily: 'var(--font-display, Syne, sans-serif)' }}
        >
          {t('management.title')}
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('management.subtitle')}
        </p>
      </div>
      <div
        className="flex rounded-lg border p-1"
        style={{ borderColor: 'var(--border)', background: 'var(--highlight)' }}
      >
        <button
          onClick={() => { onViewMode('work') }}
          className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
          style={viewBtnStyle('work')}
        >
          <LayoutGrid className="h-4 w-4" />
          {t('management.workView')}
        </button>
        <button
          onClick={() => { onViewMode('summary') }}
          className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
          style={viewBtnStyle('summary')}
        >
          <BarChart3 className="h-4 w-4" />
          {t('management.summaryView')}
        </button>
      </div>
    </div>
  )
}

interface DashboardActionButtonsProps {
  quota: CaseQuotaStatus | null
  isLoading: boolean
  onRefresh: () => void
  onStartPreview: (() => void) | undefined
  onNewCase: () => void
}

function DashboardActionButtons({
  quota,
  isLoading,
  onRefresh,
  onStartPreview,
  onNewCase,
}: DashboardActionButtonsProps): ReactElement {
  const { t } = useTranslation(['cases', 'common'])
  return (
    <div className="flex items-center gap-3">
      {quota && <CaseQuotaIndicator quota={quota} />}
      <button
        onClick={onRefresh}
        disabled={isLoading}
        className="rounded-lg border p-2.5 transition-colors disabled:opacity-50"
        style={{ borderColor: 'var(--border-hover)', color: 'var(--text-dim)' }}
        title={t('actions.refresh')}
      >
        <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
      </button>
      <button
        onClick={onStartPreview}
        className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 font-medium transition-colors"
        style={{ borderColor: 'var(--border-hover)', color: 'var(--text-dim)' }}
      >
        <Eye className="h-4 w-4" />
        {t('management.preview')}
      </button>
      <button
        onClick={onNewCase}
        disabled={!quota?.canCreateCase}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2.5 font-medium text-white shadow-lg shadow-[#4e5e82]/25 transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
      >
        <Plus className="h-4 w-4" />
        {t('management.newCase')}
      </button>
    </div>
  )
}

interface DashboardStatsBarProps {
  stats: { active: number; critical: number; totalArtifacts: number }
  searchQuery: string
  onSearch: (q: string) => void
}

function DashboardStatsBar({ stats, searchQuery, onSearch }: DashboardStatsBarProps): ReactElement {
  const { t } = useTranslation(['cases', 'common'])
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{stats.active}</span>
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('management.activeCases')}</span>
        </div>
        {stats.critical > 0 && (
          <div className="flex items-center gap-2 rounded-full bg-red-100 px-3 py-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
            <span className="text-sm font-medium text-red-700">
              {t('management.critical', { count: stats.critical })}
            </span>
          </div>
        )}
        <div className="h-6 w-px" style={{ background: 'var(--border)' }} />
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold" style={{ color: 'var(--text-dim)' }}>
            {stats.totalArtifacts.toLocaleString()}
          </span>
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('management.totalArtifacts')}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative">
          <Search
            className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { onSearch(e.target.value) }}
            placeholder={t('management.searchPlaceholder')}
            className="w-64 rounded-lg border py-2 pr-4 pl-10 text-sm transition-all focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
            style={{ borderColor: 'var(--border-hover)', background: 'var(--bg-card)', color: 'var(--text)' }}
          />
        </div>
        <button
          className="rounded-lg border p-2 transition-colors"
          style={{ borderColor: 'var(--border-hover)', color: 'var(--text-muted)' }}
        >
          <Filter className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

interface DashboardFullHeaderProps {
  viewMode: ViewMode
  stats: { active: number; critical: number; totalArtifacts: number }
  quota: CaseQuotaStatus | null
  isLoading: boolean
  searchQuery: string
  onViewMode: (mode: ViewMode) => void
  onRefresh: () => void
  onStartPreview: (() => void) | undefined
  onNewCase: () => void
  onSearch: (q: string) => void
}

export function DashboardFullHeader({
  viewMode,
  stats,
  quota,
  isLoading,
  searchQuery,
  onViewMode,
  onRefresh,
  onStartPreview,
  onNewCase,
  onSearch,
}: DashboardFullHeaderProps): ReactElement {
  return (
    <div
      className="sticky top-0 z-20 border-b"
      style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
    >
      <div className="px-6 py-4">
        <div className="mb-4 flex items-center justify-between">
          <DashboardTitleSection viewMode={viewMode} onViewMode={onViewMode} />
          <DashboardActionButtons
            quota={quota}
            isLoading={isLoading}
            onRefresh={onRefresh}
            onStartPreview={onStartPreview}
            onNewCase={onNewCase}
          />
        </div>
        {viewMode === 'work' && (
          <DashboardStatsBar stats={stats} searchQuery={searchQuery} onSearch={onSearch} />
        )}
      </div>
    </div>
  )
}

interface DashboardEmbeddedHeaderProps {
  stats: { active: number; critical: number }
  isLoading: boolean
  searchQuery: string
  quota: CaseQuotaStatus | null
  onRefresh: () => void
  onNewCase: () => void
  onSearch: (q: string) => void
}

export function DashboardEmbeddedHeader({
  stats,
  isLoading,
  searchQuery,
  quota,
  onRefresh,
  onNewCase,
  onSearch,
}: DashboardEmbeddedHeaderProps): ReactElement {
  const { t } = useTranslation(['cases', 'common'])
  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold" style={{ color: 'var(--text)' }}>{stats.active}</span>
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('management.active')}</span>
        </div>
        {stats.critical > 0 && (
          <div className="flex items-center gap-1.5 rounded-full bg-red-100 px-2 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            <span className="text-xs font-medium text-red-700">
              {t('management.critical', { count: stats.critical })}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search
            className="absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { onSearch(e.target.value) }}
            placeholder={t('management.searchPlaceholderShort')}
            className="w-40 rounded-md border py-1.5 pr-3 pl-8 text-sm focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
            style={{ borderColor: 'var(--border-hover)', background: 'var(--bg-card)', color: 'var(--text)' }}
          />
        </div>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="rounded-md border p-1.5 transition-colors disabled:opacity-50"
          style={{ borderColor: 'var(--border-hover)', color: 'var(--text-muted)' }}
          title={t('actions.refresh')}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
        <button
          onClick={onNewCase}
          disabled={!quota?.canCreateCase}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#4e5e82] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('management.new')}
        </button>
      </div>
    </div>
  )
}

type Dash = ReturnType<typeof useCaseDashboard>

export function DashboardError({ message }: { message: string }): ReactElement {
  return (
    <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
      <div className="flex items-center gap-3">
        <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
        <p className="text-sm text-red-700">{message}</p>
      </div>
    </div>
  )
}

export function LoadingCases({ label }: { label: string }): ReactElement {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="db-spinner h-8 w-8 animate-spin rounded-full border-4" />
        <p style={{ color: 'var(--text-muted)' }}>{label}</p>
      </div>
    </div>
  )
}

export function DashboardBody({
  dash,
  userName,
  onOpenCase,
  loadingLabel,
}: {
  dash: Dash
  userName: string | undefined
  onOpenCase: ((caseId: string) => void) | undefined
  loadingLabel: string
}): ReactElement | null {
  if (dash.casesLoading && dash.allCases.length === 0) {
    return <LoadingCases label={loadingLabel} />
  }
  if (dash.viewMode === 'summary') {
    return (
      <div className="p-6">
        <SummaryView
          statistics={dash.myCaseStatistics}
          loading={dash.casesLoading}
          {...(userName ? { userName } : {})}
        />
      </div>
    )
  }
  return <CaseWorkView dash={dash} onOpenCase={onOpenCase} />
}

export function DashboardModals({ dash }: { dash: Dash }): ReactElement {
  return (
    <>
      <NewCaseModal
        isOpen={dash.isNewCaseModalOpen}
        clients={dash.clients}
        quotaStatus={dash.quota}
        isCreating={dash.creating}
        isCreatingClient={dash.creatingClient}
        onSubmit={(...args: Parameters<typeof dash.handleCreateCase>) => {
          void dash.handleCreateCase(...args)
        }}
        onCreateClient={dash.handleCreateClient}
        onClose={() => { dash.setIsNewCaseModalOpen(false) }}
      />
      <EditCaseModal
        isOpen={dash.isEditCaseModalOpen}
        caseData={dash.selectedCase}
        clients={dash.clients}
        isUpdating={dash.updating}
        onSubmit={(...args: Parameters<typeof dash.handleUpdateCase>) => {
          void dash.handleUpdateCase(...args)
        }}
        onClose={() => {
          dash.setIsEditCaseModalOpen(false)
          dash.setSelectedCase(null)
        }}
      />
    </>
  )
}

export function DashboardHeader({
  dash,
  embedded,
  onStartPreview,
}: {
  dash: Dash
  embedded: boolean
  onStartPreview: (() => void) | undefined
}): ReactElement {
  if (embedded) {
    return (
      <DashboardEmbeddedHeader
        stats={dash.stats}
        isLoading={dash.isLoading}
        searchQuery={dash.searchQuery}
        quota={dash.quota}
        onRefresh={dash.handleRefresh}
        onNewCase={() => { dash.setIsNewCaseModalOpen(true) }}
        onSearch={dash.setSearchQuery}
      />
    )
  }
  return (
    <DashboardFullHeader
      viewMode={dash.viewMode}
      stats={dash.stats}
      quota={dash.quota}
      isLoading={dash.isLoading}
      searchQuery={dash.searchQuery}
      onViewMode={dash.setViewMode}
      onRefresh={dash.handleRefresh}
      onStartPreview={onStartPreview}
      onNewCase={() => { dash.setIsNewCaseModalOpen(true) }}
      onSearch={dash.setSearchQuery}
    />
  )
}
